// Distance calculation service for delivery fee automation
// Uses Google Maps Distance Matrix API to calculate distances between offices and delivery locations

// Company office locations for distance calculations
export const OFFICE_LOCATIONS = {
  angola: {
    name: 'Angola Office',
    address: '15 Kodak Ln, Angola, IN 46703',
    coordinates: { lat: 41.634494, lng: -84.9985918 }
  },
  elkhart: {
    name: 'Elkhart Office', 
    address: '56551 Mars Dr, Elkhart, IN 46516',
    coordinates: { lat: 41.72282, lng: -85.9737919 }
  }
};

// Delivery fee calculation settings
export const DELIVERY_FEE_CONFIG = {
  baseRate: 2.00,        // Flat rate per mile
  minimumFee: 10,        // Minimum delivery fee
  maximumFee: 200,       // Maximum delivery fee to prevent extreme costs
  freeDeliveryRadius: 0, // No free delivery - charge $2/mile from mile 1
  maxDeliveryDistance: 30 // Maximum delivery distance (30-minute drive time ≈ 30 miles)
};

/**
 * Calculate delivery fee based on distance using flat $2/mile rate
 * @param {number} miles - Distance in miles
 * @returns {number} - Calculated delivery fee
 */
export const calculateDeliveryFee = (miles) => {
  // Round up to nearest mile for consistency
  const roundedMiles = Math.ceil(miles);
  
  // Free delivery within radius
  if (roundedMiles <= DELIVERY_FEE_CONFIG.freeDeliveryRadius) {
    return 0;
  }
  
  // Calculate fee using flat rate
  let fee = roundedMiles * DELIVERY_FEE_CONFIG.baseRate;
  
  // Apply minimum and maximum constraints
  fee = Math.max(fee, DELIVERY_FEE_CONFIG.minimumFee);
  fee = Math.min(fee, DELIVERY_FEE_CONFIG.maximumFee);
  
  // Return precise calculation (no rounding to $5)
  return Math.round(fee * 100) / 100; // Round to nearest cent
};

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param {object} coord1 - {lat, lng}
 * @param {object} coord2 - {lat, lng}
 * @returns {number} - Distance in miles
 */
export const calculateHaversineDistance = (coord1, coord2) => {
  const R = 3959; // Earth's radius in miles
  const dLat = toRadians(coord2.lat - coord1.lat);
  const dLng = toRadians(coord2.lng - coord1.lng);
  
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(coord1.lat)) * Math.cos(toRadians(coord2.lat)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const toRadians = (degrees) => degrees * (Math.PI / 180);

/**
 * Get coordinates for an address using geocoding
 * @param {string} address - Full address string
 * @returns {Promise<object>} - {lat, lng} coordinates
 */
export const geocodeAddress = async (address) => {
  // 1. Primary: real geocoding via the backend proxy (Google Geocoding API).
  //    Accurately resolves ANY address, so out-of-area deliveries can't slip
  //    through on a ZIP we happen not to have hardcoded.
  try {
    const resp = await fetch(`/api/geocode?address=${encodeURIComponent(address)}`);
    if (resp.ok) {
      const data = await resp.json();
      if (typeof data.lat === 'number' && typeof data.lng === 'number') {
        return { lat: data.lat, lng: data.lng };
      }
    }
    // 503 (key not configured) / 404 / 5xx fall through to the local table below.
  } catch (e) {
    // Network/parse error — fall through to the local table.
  }

  // 2. Fallback: local ZIP → coordinate table. Covers the core service area
  //    even when the geocoding API is unavailable.
  const zipMatch = address.match(/\b(\d{5})\b/);
  if (!zipMatch) {
    const err = new Error('Could not determine coordinates for this address');
    err.code = 'GEOCODE_UNRESOLVED';
    throw err;
  }

  const zip = zipMatch[1];

  // Fallback ZIP code to coordinate mapping for Indiana/Michigan area
  const zipCoordinates = {
    // Angola region
    '46703': { lat: 41.634494, lng: -84.9985918 }, // Angola
    '46737': { lat: 41.675, lng: -85.025 },        // Fremont
    '46742': { lat: 41.64, lng: -85.0 },           // Hamilton
    '46705': { lat: 41.52, lng: -85.15 },          // Ashley
    '46779': { lat: 41.695, lng: -85.162 },        // Pleasant Lake
    '46747': { lat: 41.58, lng: -85.13 },          // Hudson
    '46706': { lat: 41.369, lng: -85.052 },        // Auburn
    '46755': { lat: 41.436, lng: -85.264 },        // Kendallville
    '46793': { lat: 41.362, lng: -85.017 },        // Waterloo
    
    // Elkhart region  
    '46516': { lat: 41.72282, lng: -85.9737919 },  // Elkhart
    '46514': { lat: 41.73, lng: -85.98 },          // Elkhart
    '46515': { lat: 41.71, lng: -85.95 },          // Elkhart
    '46517': { lat: 41.74, lng: -86.0 },           // Elkhart
    '46526': { lat: 41.582, lng: -85.855 },        // Goshen
    '46528': { lat: 41.59, lng: -85.87 },          // Goshen
    '46540': { lat: 41.687, lng: -85.703 },        // Middlebury
    '46507': { lat: 41.666, lng: -85.788 },        // Bristol
    '46573': { lat: 41.405, lng: -86.001 },        // Wakarusa
    '46550': { lat: 41.442, lng: -85.998 },        // Nappanee
    '46544': { lat: 41.662, lng: -86.158 },        // Mishawaka
    '46545': { lat: 41.68, lng: -86.18 },          // Mishawaka
    
    // Major cities
    '46804': { lat: 41.130, lng: -85.129 },        // Fort Wayne
    '46805': { lat: 41.100, lng: -85.098 },        // Fort Wayne
    '46806': { lat: 41.067, lng: -85.139 },        // Fort Wayne
    '46807': { lat: 41.116, lng: -85.175 },        // Fort Wayne
    '46808': { lat: 41.076, lng: -85.074 },        // Fort Wayne
    '46809': { lat: 41.141, lng: -85.042 },        // Fort Wayne
    '46815': { lat: 41.041, lng: -85.152 },        // Fort Wayne
    '46816': { lat: 41.003, lng: -85.099 },        // Fort Wayne
    '46601': { lat: 41.676, lng: -86.252 },        // South Bend
    '46614': { lat: 41.63, lng: -86.25 },          // South Bend
    '46615': { lat: 41.68, lng: -86.28 },          // South Bend
    '46616': { lat: 41.70, lng: -86.24 },          // South Bend
    '46617': { lat: 41.64, lng: -86.29 },          // South Bend
    '46619': { lat: 41.61, lng: -86.27 },          // South Bend
    '46628': { lat: 41.69, lng: -86.21 },          // South Bend
    '46580': { lat: 41.236, lng: -85.853 },        // Warsaw
    '46581': { lat: 41.25, lng: -85.87 },          // Warsaw
    '46725': { lat: 41.168, lng: -85.484 },        // Columbia City
    
    // Michigan
    '49091': { lat: 41.813, lng: -85.437 },        // Sturgis
    '49036': { lat: 41.999, lng: -85.058 },        // Coldwater
    '49120': { lat: 41.830, lng: -86.254 },        // Niles
    '49047': { lat: 41.984, lng: -86.109 },        // Dowagiac
    
    // Additional areas
    '46506': { lat: 41.446, lng: -86.089 },        // Bremen
    '46795': { lat: 41.540, lng: -85.308 },        // Wolcottville
    '46761': { lat: 41.525, lng: -85.417 },        // LaGrange
  };
  
  const coordinates = zipCoordinates[zip];
  if (!coordinates) {
    const err = new Error(`Could not determine coordinates for ZIP code: ${zip}`);
    err.code = 'GEOCODE_UNRESOLVED';
    throw err;
  }

  return coordinates;
};

/**
 * Determine which office should serve a location and calculate distances to both
 * @param {string} city - City name
 * @param {string} state - State abbreviation  
 * @param {string} zipCode - ZIP code
 * @returns {Promise<object>} - {suggestedOffice, distances: {angola: X, elkhart: Y}, withinRange}
 */
export const determineServingOffice = async (city, state, zipCode) => {
  try {
    const address = `${city}, ${state} ${zipCode}`;
    const destinationCoords = await geocodeAddress(address);
    
    // Calculate distance from each office
    const distances = {
      angola: calculateHaversineDistance(OFFICE_LOCATIONS.angola.coordinates, destinationCoords),
      elkhart: calculateHaversineDistance(OFFICE_LOCATIONS.elkhart.coordinates, destinationCoords)
    };
    
    // Determine suggested office (closest one)
    const suggestedOffice = distances.angola <= distances.elkhart ? 'angola' : 'elkhart';
    const closestDistance = Math.min(distances.angola, distances.elkhart);
    
    return {
      suggestedOffice,
      distances: {
        angola: Math.round(distances.angola * 10) / 10,
        elkhart: Math.round(distances.elkhart * 10) / 10
      },
      withinRange: closestDistance <= DELIVERY_FEE_CONFIG.maxDeliveryDistance,
      closestDistance: Math.round(closestDistance * 10) / 10
    };
  } catch (error) {
    console.error('Error determining serving office:', error);
    throw new Error(`Could not determine serving office: ${error.message}`);
  }
};

/**
 * Calculate delivery fee for a location based on distance from specified office
 * @param {string} city - City name
 * @param {string} state - State abbreviation  
 * @param {string} zipCode - ZIP code
 * @param {string} assignedOffice - Optional: 'angola' or 'elkhart'. If not provided, uses nearest office
 * @returns {Promise<object>} - {fee, distance, nearestOffice, calculations, withinRange}
 */
export const calculateLocationDeliveryFee = async (city, state, zipCode, assignedOffice = null) => {
  try {
    const address = `${city}, ${state} ${zipCode}`;
    const destinationCoords = await geocodeAddress(address);
    
    let servingOffice, distance, officeName;
    
    if (assignedOffice) {
      // Use specified office
      if (!OFFICE_LOCATIONS[assignedOffice]) {
        throw new Error(`Invalid office: ${assignedOffice}`);
      }
      servingOffice = assignedOffice;
      officeName = OFFICE_LOCATIONS[assignedOffice].name;
      distance = calculateHaversineDistance(OFFICE_LOCATIONS[assignedOffice].coordinates, destinationCoords);
    } else {
      // Calculate distance from each office and find nearest
      const distances = Object.entries(OFFICE_LOCATIONS).map(([key, office]) => ({
        office: key,
        officeName: office.name,
        distance: calculateHaversineDistance(office.coordinates, destinationCoords)
      }));
      
      const nearest = distances.reduce((min, current) => 
        current.distance < min.distance ? current : min
      );
      
      servingOffice = nearest.office;
      officeName = nearest.officeName;
      distance = nearest.distance;
    }
    
    // Check if within delivery range
    const withinRange = distance <= DELIVERY_FEE_CONFIG.maxDeliveryDistance;
    
    if (!withinRange) {
      throw new Error(`Location is ${Math.round(distance * 10) / 10} miles from ${officeName}, which exceeds our ${DELIVERY_FEE_CONFIG.maxDeliveryDistance}-mile delivery range`);
    }
    
    // Calculate delivery fee based on distance
    const fee = calculateDeliveryFee(distance);
    
    return {
      fee,
      distance: Math.round(distance * 10) / 10, // Round to 1 decimal
      nearestOffice: officeName,
      officeKey: servingOffice,
      withinRange,
      calculations: {
        appliedRate: DELIVERY_FEE_CONFIG.baseRate,
        beforeConstraints: Math.ceil(distance) * DELIVERY_FEE_CONFIG.baseRate,
        isFreeDelivery: distance <= DELIVERY_FEE_CONFIG.freeDeliveryRadius,
        isMinimumApplied: fee === DELIVERY_FEE_CONFIG.minimumFee,
        isMaximumApplied: fee === DELIVERY_FEE_CONFIG.maximumFee
      }
    };
  } catch (error) {
    console.error('Error calculating delivery fee:', error);
    throw new Error(`Could not calculate delivery fee: ${error.message}`);
  }
};

/**
 * Get formatted explanation of delivery fee calculation
 * @param {object} calculation - Result from calculateLocationDeliveryFee
 * @returns {string} - Human-readable explanation
 */
export const getDeliveryFeeExplanation = (calculation) => {
  const { distance, nearestOffice, fee, calculations } = calculation;
  
  if (calculations.isFreeDelivery) {
    return `Free delivery (${distance} mi from ${nearestOffice} - within ${DELIVERY_FEE_CONFIG.freeDeliveryRadius} mile radius)`;
  }
  
  let explanation = `${distance} mi from ${nearestOffice} × $${calculations.appliedRate}/mi = $${calculations.beforeConstraints.toFixed(2)}`;
  
  if (calculations.isMinimumApplied) {
    explanation += ` → $${fee} (minimum fee applied)`;
  } else if (calculations.isMaximumApplied) {
    explanation += ` → $${fee} (maximum fee applied)`;
  } else {
    explanation += ` → $${fee}`;
  }
  
  return explanation;
}; 